import * as Yup from 'yup';
import { DATA_AGGREGATOR_OPTIONS, FieldData, Label, Select, TemplateData, TextField, useForm, withStyles } from 'vopay';
import { Toggle } from 'UI';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { useBillingPackages } from '@/providers/BillingPackages.provider';
import usePlatformUsageOptions from './usePlatformUsageOptions';
import type { SettingsFormValues } from './types';

import styles from './SettingsStep.module.scss';

const cn = withStyles(styles);

const INITIAL_VALUES: SettingsFormValues = {
  isSubAccountsEnabled: false,
  isClientAccountsEnabled: false,
  platformUsage: '',
  accountSystem: '',
  dataAggregator: '',
  packageType: '',
};

// The step lays fields out as label-left / control-right rows rather than the stacked default.
// Only the row layout is ours: the label and its required marker come from the form layer, and
// each control renders its own error, so these rows look like every other form in the app.
function SettingRow({ label, inputState, fieldName, template }: TemplateData) {
  return (
    <div className={cn('settingRow')}>
      <Label label={label} required={inputState === 'required'} isBold={false} padding="0px" htmlFor={fieldName} />
      <div className={cn('rowControl')}>{template}</div>
    </div>
  );
}

type UseSettingsFormConfigArgs = {
  // The Details step's selected country; changing it invalidates any picked billing package.
  country: string;
  /**
   * Sub-account mode (ID-13797): every setting is inherited from the parent, so the fields hide —
   * keeping their values for a switch back to a top-level account — and none of them validates.
   */
  isSubAccountMode: boolean;
  onSubmit: () => void;
};

export default function useSettingsFormConfig({ country, isSubAccountMode, onSubmit }: UseSettingsFormConfigArgs) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Settings' });

  const { canSeeBusinessCase, canAccessCustomServiceRequirements, partnerBillsAccount } =
    useModuleFeatureFlag('accounts');

  const { billingPackages } = useBillingPackages();

  const showBillingPackage = !canAccessCustomServiceRequirements && !partnerBillsAccount && !isSubAccountMode;

  const platformUsageOptions = usePlatformUsageOptions();

  // Platform Usage, Account System and Data Aggregator are required only for a business-case
  // partner (ID-13331 decision, overriding the spec's blanket "required").
  const businessCaseInputState = canSeeBusinessCase ? 'required' : 'optional';

  const requiredForBusinessCase = (message: string) =>
    canSeeBusinessCase ? Yup.string().required(message) : Yup.string();

  const dataAggregatorOptions = useMemo(
    () => Object.entries(DATA_AGGREGATOR_OPTIONS).map(([value, label]) => ({ value, label })),
    [],
  );

  const billingPackageOptions = useMemo(
    () => Object.entries(billingPackages.dropDownOptions).map(([value, label]) => ({ value, label })),
    [billingPackages.dropDownOptions],
  );

  const topLevelOnly = () => !isSubAccountMode;

  const toggleField = (
    fieldName: 'isSubAccountsEnabled' | 'isClientAccountsEnabled',
    label: string,
  ): FieldData<SettingsFormValues> => ({
    fieldName,
    label: () => label,
    getInputState: () => 'optional',
    showField: topLevelOnly,
    renderField: ({ ctx }) => (
      <Toggle
        name={fieldName}
        checked={ctx.values[fieldName]}
        onChange={(event) => ctx.setFieldValue(fieldName, event.target.checked)}
      />
    ),
  });

  const config: FieldData<SettingsFormValues>[] = [
    toggleField('isSubAccountsEnabled', t('subAccounts', { defaultValue: 'Sub-Accounts' })),
    toggleField('isClientAccountsEnabled', t('clientAccounts', { defaultValue: 'Client Accounts' })),
    {
      fieldName: 'platformUsage',
      label: () => t('platformUsage', { defaultValue: 'Platform Usage' }),
      getInputState: () => businessCaseInputState,
      showField: topLevelOnly,
      validate: () =>
        requiredForBusinessCase(t('platformUsageRequired', { defaultValue: 'Please select platform usage.' })),
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          options={platformUsageOptions}
          placeholder={t('selectPlaceholder', { defaultValue: 'Select' })}
          sort={false}
          {...getFormikData<SettingsFormValues['platformUsage']>({})}
        />
      ),
    },
    {
      fieldName: 'accountSystem',
      label: () => t('accountSystem', { defaultValue: 'Account System' }),
      getInputState: () => businessCaseInputState,
      showField: topLevelOnly,
      validate: () =>
        requiredForBusinessCase(t('accountSystemRequired', { defaultValue: 'Please enter the account system.' })),
      renderField: ({ getFormikData }) => (
        <TextField
          variant="outlineShadow"
          placeholder={t('accountSystemPlaceholder', { defaultValue: 'eg: Sage, Quickbooks, TurnKey, etc.' })}
          {...getFormikData<SettingsFormValues['accountSystem']>({})}
        />
      ),
    },
    {
      fieldName: 'dataAggregator',
      label: () => t('dataAggregator', { defaultValue: 'Data Aggregator' }),
      getInputState: () => businessCaseInputState,
      showField: topLevelOnly,
      validate: () =>
        requiredForBusinessCase(t('dataAggregatorRequired', { defaultValue: 'Please select a data aggregator.' })),
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          options={dataAggregatorOptions}
          placeholder={t('selectPlaceholder', { defaultValue: 'Select' })}
          sort={false}
          {...getFormikData<SettingsFormValues['dataAggregator']>({})}
        />
      ),
    },
    {
      fieldName: 'packageType',
      label: () => t('billingPackage', { defaultValue: 'Billing Package' }),
      // 'disabled' when the row is hidden: that is what suppresses this field's validation.
      getInputState: () => (showBillingPackage ? 'required' : 'disabled'),
      showField: () => showBillingPackage,
      validate: () =>
        Yup.string().required(t('billingPackageRequired', { defaultValue: 'Please select a billing package.' })),
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          options={billingPackageOptions}
          placeholder={t('billingPackagePlaceholder', { defaultValue: 'Select a Billing Package' })}
          {...getFormikData<SettingsFormValues['packageType']>({})}
        />
      ),
    },
  ];

  const { ctx, renderFormElements } = useForm<SettingsFormValues>({
    initialValues: INITIAL_VALUES,
    config,
    onSubmit,
  });

  // The billing-package options are country-scoped, so a country change invalidates the pick —
  // otherwise a wrong-country PackageID passes required() while Review renders nothing for it.
  const previousCountryRef = useRef(country);

  useEffect(() => {
    if (previousCountryRef.current === country) return;

    previousCountryRef.current = country;
    if (ctx.values.packageType) ctx.setFieldValue('packageType', '');
  }, [country]);

  return {
    ctx,
    renderFormElements: () =>
      renderFormElements({ config, override: (templateData) => <SettingRow {...templateData} /> }),
  };
}
